import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Landmark, ArrowDownToLine, CalendarClock, CheckCircle2, AlertTriangle } from "lucide-react";
import { euro, dateTime } from "@/lib/format";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch (e) { return iso; }
};

const PAYOUT_STATUS = {
  paid: { label: "Arrivé sur votre banque", cls: "bg-[hsl(160_84%_32%)] text-white border-0" },
  pending: { label: "En préparation", cls: "bg-[hsla(38,92%,50%,0.15)] text-[hsl(38_92%_38%)] border-0" },
  in_transit: { label: "En route vers votre banque", cls: "bg-[hsla(38,92%,50%,0.15)] text-[hsl(38_92%_38%)] border-0" },
  failed: { label: "Échoué", cls: "bg-destructive/15 text-destructive border-0" },
  canceled: { label: "Annulé", cls: "bg-secondary text-muted-foreground border-0" },
};

/**
 * Suivi des retraits du vendeur : chaque retrait (MIHOD -> compte Stripe) avec la date estimée
 * d'arrivée sur la banque, puis les virements réels Stripe -> banque (payouts du compte connecté).
 */
export const WithdrawalTracking = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/wallet/withdrawals");
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return <Skeleton className="h-24 rounded-2xl" data-testid="withdrawals-loading" />;
  if (!data || (data.withdrawals.length === 0 && data.bank_payouts.length === 0)) return null;

  return (
    <div data-testid="withdrawal-tracking">
      <h2 className="font-display text-lg mb-2 flex items-center gap-2"><ArrowDownToLine className="h-4 w-4 text-primary" /> Mes retraits</h2>
      <Card className="rounded-2xl overflow-hidden divide-y divide-border">
        {data.withdrawals.map((w) => {
          const reversed = w.status === "reversed" || (w.amount_reversed || 0) > 0;
          return (
            <div key={w.id} className="p-4 flex items-start justify-between gap-3" data-testid={`withdrawal-${w.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium">{euro(w.amount)} <span className="text-muted-foreground font-normal">retirés le {dateTime(w.created_at)}</span></p>
                {reversed ? (
                  <p className="text-[11px] text-destructive flex items-center gap-1 mt-0.5"><AlertTriangle className="h-3 w-3" /> Virement inversé par Stripe — contactez le support.</p>
                ) : (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5" data-testid={`withdrawal-eta-${w.id}`}>
                    <CalendarClock className="h-3 w-3" /> Arrivée estimée sur votre banque : <strong className="text-foreground">{fmtDate(w.estimated_arrival)}</strong>
                  </p>
                )}
              </div>
              <Badge className={reversed ? PAYOUT_STATUS.failed.cls : PAYOUT_STATUS.paid.cls}>{reversed ? "Inversé" : "Versé sur Stripe"}</Badge>
            </div>
          );
        })}
      </Card>

      {data.connect_onboarded && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <Landmark className="h-3.5 w-3.5" /> Virements Stripe vers votre banque
            {data.bank_account && <span>· {data.bank_account.bank_name || "Banque"} ••••{data.bank_account.last4} ({data.bank_account.currency})</span>}
          </p>
          {data.bank_payouts.length === 0 ? (
            <p className="text-[11px] text-muted-foreground" data-testid="bank-payouts-empty">
              Stripe regroupe vos retraits et les vire {data.schedule?.interval === "daily" ? "chaque jour ouvré" : "automatiquement"}
              {data.schedule?.delay_days ? ` (délai bancaire : ${data.schedule.delay_days} jours ouvrés)` : ""}. Le premier virement apparaîtra ici.
            </p>
          ) : (
            <Card className="rounded-2xl overflow-hidden divide-y divide-border" data-testid="bank-payouts">
              {data.bank_payouts.map((p) => {
                const st = PAYOUT_STATUS[p.status] || { label: p.status, cls: "bg-secondary text-muted-foreground border-0" };
                return (
                  <div key={p.id} className="p-3 flex items-center justify-between gap-3" data-testid={`bank-payout-${p.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{euro(p.amount)}</p>
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                        {p.status === "paid" ? <CheckCircle2 className="h-3 w-3 text-[hsl(160_84%_38%)]" /> : <CalendarClock className="h-3 w-3" />}
                        {p.status === "paid" ? "Arrivé le" : "Arrivée prévue le"} {fmtDate(p.arrival_date)}
                      </p>
                      {p.failure_message && <p className="text-[10px] text-destructive">{p.failure_message}</p>}
                    </div>
                    <Badge className={st.cls}>{st.label}</Badge>
                  </div>
                );
              })}
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
